import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { RestService } from "../../../services/rest.service";
import { RestUtil } from "../../../services/rest.util";
import { PaginatedResponse } from "../../../interface/PaginatedResponse";
import { MasterOrder } from "../interface/master-order.interface";
import { OrderStatus } from "../enum/order-status.enum";
import { CreateMasterOrderDto } from "../dto/create-master-order.dto";
import { AutoRenewMandate } from "../interface/auto-renew-mandate.interface";
import { PaymentMethod } from "../enum/payment-method.enum";

export interface MasterOrderQuery {
    orgId?: string;
    orderStatus?: OrderStatus;
    startDate?: number;
    endDate?: number;
    conflictOnly?: boolean;
    hasRazorpaySubscription?: boolean;
    razorpaySubscriptionId?: string;
}

export interface MasterPaymentMethodsResponse {
    paymentMethods: PaymentMethod[];
    cashEnabled: boolean;
    zohoPaymentAccountId: string | null;
}

export interface PaymentReminderResult {
    orderId: string;
    orderNumber: string;
    orderStatus: OrderStatus | string;
    paymentMethod: PaymentMethod | string;
    paymentUrl: string | null;
    recipients: string[];
}

/**
 * Admin-side access to orders across all organisations (NestJS `master/order` routes).
 * Used by the master app's Orders page to view payments and resolve activation conflicts.
 */
@Injectable({
    providedIn: 'root'
})
export class OrderMasterApiService {

    static readonly ApiUrl: string = 'master/order';

    constructor(private restService: RestService, private http: HttpClient) { }

    async readAll(page: number = 1, filters: MasterOrderQuery = {}): Promise<PaginatedResponse<MasterOrder>> {
        return (await this.restService.send<PaginatedResponse<MasterOrder>>({
            url: OrderMasterApiService.ApiUrl,
            method: 'GET',
            query: {
                page: page,
                paginated: true,
                orgId: filters.orgId || undefined,
                orderStatus: filters.orderStatus || undefined,
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined,
                conflictOnly: filters.conflictOnly ? true : undefined,
                hasRazorpaySubscription: filters.hasRazorpaySubscription ? 'true' : undefined,
                razorpaySubscriptionId: filters.razorpaySubscriptionId || undefined,
            }
        })).data;
    }

    async readOne(orderId: string): Promise<MasterOrder> {
        return (await this.restService.send<MasterOrder>({
            url: OrderMasterApiService.ApiUrl + '/' + orderId,
            method: 'GET'
        })).data;
    }

    /** Payment methods available to the current master user (Cash only if Zoho account configured). */
    async getPaymentMethods(): Promise<MasterPaymentMethodsResponse> {
        return (await this.restService.send<MasterPaymentMethodsResponse>({
            url: OrderMasterApiService.ApiUrl + '/payment-methods',
            method: 'GET',
            throwError: true,
        })).data;
    }

    /**
     * Create an order from the master app.
     * `razorpay_subscription` → mandate + payment link; `razorpay` → one-time; other methods unchanged.
     */
    async createMasterOrder(body: CreateMasterOrderDto): Promise<MasterOrder> {
        return (await this.restService.send<MasterOrder>({
            url: OrderMasterApiService.ApiUrl,
            method: 'POST',
            body,
            throwError: true,
        })).data;
    }

    /**
     * Soft-delete an order (sets deleteTime). Excluded from listings thereafter.
     */
    async delete(orderId: string): Promise<MasterOrder> {
        return (await this.restService.send<MasterOrder>({
            url: OrderMasterApiService.ApiUrl + '/' + orderId,
            method: 'DELETE',
            throwError: true,
        })).data;
    }

    /** Resolve a conflict by creating the missing plan purchase for a paid order. */
    async activatePlan(orderId: string): Promise<MasterOrder> {
        return (await this.restService.send<MasterOrder>({
            url: OrderMasterApiService.ApiUrl + '/' + orderId + '/activate-plan',
            method: 'POST',
            throwError: true,
        })).data;
    }

    /** Manually record an offline / external payment against an unpaid order. */
    async recordPayment(
        orderId: string,
        body: { paymentDate: string; paymentMethod: string; referenceId: string },
    ): Promise<MasterOrder> {
        return (await this.restService.send<MasterOrder>({
            url: OrderMasterApiService.ApiUrl + '/' + orderId + '/record-payment',
            method: 'POST',
            body,
            throwError: true,
        })).data;
    }

    /** Generate the Zoho invoice for a paid order that has no invoice yet. */
    async generateInvoice(orderId: string): Promise<MasterOrder> {
        return (await this.restService.send<MasterOrder>({
            url: OrderMasterApiService.ApiUrl + '/' + orderId + '/generate-invoice',
            method: 'POST',
            throwError: true,
        })).data;
    }

    /**
     * Email a payment reminder to org admins for an initiated order.
     * Razorpay orders include a Pay now link; other methods get a reminder only.
     */
    async sendPaymentReminder(orderId: string): Promise<PaymentReminderResult> {
        return (await this.restService.send<PaymentReminderResult>({
            url: OrderMasterApiService.ApiUrl + '/' + orderId + '/payment-reminder',
            method: 'POST',
            throwError: true,
        })).data;
    }

    async readAutoRenew(orgId?: string): Promise<AutoRenewMandate[]> {
        return (await this.restService.send<AutoRenewMandate[]>({
            url: OrderMasterApiService.ApiUrl + '/auto-renew',
            method: 'GET',
            query: { orgId: orgId || undefined },
        })).data || [];
    }

    async cancelAutoRenew(razorpaySubscriptionId: string): Promise<void> {
        await this.restService.send({
            url: OrderMasterApiService.ApiUrl + '/auto-renew/' + razorpaySubscriptionId,
            method: 'DELETE',
            throwError: true,
        });
    }

    /** Sync Razorpay subscription payment status for a pending order. */
    async recheckPayment(orderId: string): Promise<MasterOrder> {
        return (await this.restService.send<MasterOrder>({
            url: OrderMasterApiService.ApiUrl + '/' + orderId + '/recheck-payment',
            method: 'POST',
            throwError: true,
        })).data;
    }

    /**
     * Download the invoice PDF for an order and save it. RestService is JSON-only, so this fetches
     * the bytes via HttpClient (responseType blob) using the same auth headers RestUtil applies.
     */
    async downloadInvoice(orderId: string, fileName: string): Promise<void> {
        const url = `${RestUtil.BaseUrl}${OrderMasterApiService.ApiUrl}/${orderId}/invoice`;

        let headers = new HttpHeaders().append('Authorization', 'Bearer ' + RestUtil.AuthToken);
        if (RestUtil.Account_id) {
            headers = headers.append('account_id', RestUtil.Account_id);
        }
        if (RestUtil.AccountType) {
            headers = headers.append('accountType', RestUtil.AccountType);
        }
        if (RestUtil.TokenId) {
            headers = headers.append('tokenId', RestUtil.TokenId);
        }

        const blob = await firstValueFrom(this.http.get(url, { headers, responseType: 'blob' }));

        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName || 'invoice.pdf';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(objectUrl);
    }
}
